import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { AlertTriangle, CheckCircle2, XCircle, MinusCircle, Loader2, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type Flow = "attended" | "noshow";
type Channel = "whatsapp" | "email" | null;

interface Decision {
  ticket_id: string;
  user_id: string;
  first_name: string | null;
  flow: Flow;
  channel: Channel;
  code?: string | null;
  credits?: number;
  skipped?: boolean;
  reason?: string;
  retry_send_only?: boolean;
  outcome?: string;
}

interface ProcessResponse {
  mode: "preview" | "execute";
  forced_by_dry_run?: boolean;
  coverage: { total_confirmed: number; present: number; no_show: number; unmarked: number };
  decisions: Decision[];
  counts?: { sent: number; failed: number; skipped: number };
}

interface Props {
  eventId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SKIP_LABELS: Record<string, string> = {
  not_first_timer: "Not a first-timer",
  user_already_treated: "User already received follow-up",
  already_sent: "Already sent",
  noshow_non_gbp_event: "No-show refund unavailable (non-GBP event)",
  no_contact: "No contact method on file",
  no_contact_retry: "No contact method for retry",
};

function friendlyReason(reason?: string): string {
  if (!reason) return "Skipped";
  if (reason.startsWith("noshow_no_refund_method:")) {
    const method = reason.split(":")[1] || "unknown";
    return `No-show refund unavailable (payment: ${method})`;
  }
  return SKIP_LABELS[reason] || reason;
}

function actionLabel(d: Decision): string {
  if (d.skipped) return `Skipped: ${friendlyReason(d.reason)}`;
  if (d.retry_send_only) {
    const ch = d.channel === "email" ? "email" : "WhatsApp";
    return `Message retry (already refunded) · ${ch}`;
  }
  const ch = d.channel === "email" ? "Email" : d.channel === "whatsapp" ? "WhatsApp" : "—";
  if (d.flow === "attended") return `20% code + ${ch}`;
  const credits = d.credits ?? 0;
  return `${credits} credits refund + ${ch}`;
}

function DecisionRow({ d, showOutcome }: { d: Decision; showOutcome?: boolean }) {
  const isSkipped = !!d.skipped;
  const isRetry = !!d.retry_send_only;
  const flowBadge =
    d.flow === "attended" ? (
      <Badge variant="outline" className="bg-success/10 text-success border-success/30 text-[10px]">
        Attended
      </Badge>
    ) : (
      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30 text-[10px]">
        No-show
      </Badge>
    );

  const Icon = isSkipped ? MinusCircle : isRetry ? Loader2 : d.flow === "attended" ? Sparkles : CheckCircle2;

  const outcomeBadge = showOutcome && d.outcome ? (
    <Badge
      variant="outline"
      className={cn(
        "text-[10px] ml-auto",
        d.outcome === "sent" && "bg-success/10 text-success border-success/30",
        d.outcome === "failed" && "bg-destructive/10 text-destructive border-destructive/30",
        d.outcome?.startsWith("skipped") && "bg-muted text-muted-foreground border-border",
      )}
    >
      {d.outcome}
    </Badge>
  ) : null;

  return (
    <div className="flex items-start gap-2 py-2 border-b last:border-b-0">
      <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", isSkipped ? "text-muted-foreground" : "text-foreground")} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium truncate">{d.first_name || "Unknown"}</span>
          {flowBadge}
          {outcomeBadge}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{actionLabel(d)}</p>
      </div>
    </div>
  );
}

export function FirstTimerFollowupsDialog({ eventId, open, onOpenChange }: Props) {
  const queryClient = useQueryClient();
  const [preview, setPreview] = useState<ProcessResponse | null>(null);
  const [executeResult, setExecuteResult] = useState<ProcessResponse | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset all state whenever the dialog closes; auto-fire preview when it opens.
  useEffect(() => {
    if (!open) {
      setPreview(null);
      setExecuteResult(null);
      setError(null);
      setLoadingPreview(false);
      setExecuting(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingPreview(true);
      setError(null);
      try {
        const { data, error: fnErr } = await supabase.functions.invoke("process-first-timer-followups", {
          body: { eventId, mode: "preview" },
        });
        if (cancelled) return;
        if (fnErr) throw fnErr;
        if ((data as any)?.error) throw new Error((data as any).error);
        setPreview(data as ProcessResponse);
      } catch (e: any) {
        if (!cancelled) setError(e.message || "Failed to load preview");
      } finally {
        if (!cancelled) setLoadingPreview(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId]);

  async function runExecute() {
    setExecuting(true);
    setError(null);
    try {
      const { data, error: fnErr } = await supabase.functions.invoke("process-first-timer-followups", {
        body: { eventId, mode: "execute" },
      });
      if (fnErr) throw fnErr;
      if ((data as any)?.error) throw new Error((data as any).error);
      setExecuteResult(data as ProcessResponse);
      queryClient.invalidateQueries({ queryKey: ["checkin-tickets", eventId] });
      queryClient.invalidateQueries({ queryKey: ["first-timer-prior-runs", eventId] });
      toast.success("Follow-ups processed");
    } catch (e: any) {
      setError(e.message || "Failed to execute");
      toast.error(e.message || "Failed to execute");
    } finally {
      setExecuting(false);
    }
  }

  const { data: priorRuns } = useQuery({
    queryKey: ["first-timer-prior-runs", eventId],
    enabled: open,
    queryFn: async () => {
      const { data, error: qErr } = await supabase
        .from("notification_processing_runs")
        .select("id, triggered_at, triggered_by, sent_count, failed_count, skipped_count")
        .filter("details->>event_id", "eq", eventId)
        .order("triggered_at", { ascending: false })
        .limit(5);
      if (qErr) throw qErr;
      return data || [];
    },
  });

  const view = executeResult || preview;
  const showingResults = !!executeResult;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>First-timer follow-ups</DialogTitle>
          <DialogDescription>
            Preview and send WhatsApp/email follow-ups to first-time attendees for this event.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Prior runs */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1.5">Prior runs</p>
            {priorRuns && priorRuns.length > 0 ? (
              <div className="space-y-1">
                {priorRuns.map((r: any) => (
                  <div key={r.id} className="flex items-center gap-2 text-xs px-2 py-1.5 rounded bg-muted/50">
                    <span className="font-medium">
                      {r.triggered_at ? format(new Date(r.triggered_at), "MMM d, HH:mm") : "—"}
                    </span>
                    <span className="text-success">{r.sent_count ?? 0} sent</span>
                    <span className="text-destructive">{r.failed_count ?? 0} failed</span>
                    <span className="text-muted-foreground">{r.skipped_count ?? 0} skipped</span>
                    {r.triggered_by && (
                      <Badge variant="outline" className="ml-auto text-[10px]">
                        {r.triggered_by}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground italic">No prior runs for this event.</p>
            )}
          </div>

          {loadingPreview && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
              Loading preview...
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {view && (
            <>
              {/* Coverage */}
              <div className="rounded-lg border p-3 bg-muted/30">
                <p className="text-sm">
                  <span className="font-semibold">{view.coverage.total_confirmed}</span> confirmed tickets:{" "}
                  <span className="text-success font-medium">{view.coverage.present} present</span> ·{" "}
                  <span className="text-warning font-medium">{view.coverage.no_show} no-show</span> ·{" "}
                  <span className="text-muted-foreground">
                    {view.coverage.unmarked} unmarked {view.coverage.unmarked > 0 && "(will be skipped)"}
                  </span>
                </p>
              </div>

              {view.coverage.unmarked > 0 && !showingResults && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Attendance marking looks incomplete</AlertTitle>
                  <AlertDescription>
                    Chase the host before executing, or proceed and re-run later. Re-running is safe:
                    it picks up newly marked tickets and never double-sends or double-refunds.
                  </AlertDescription>
                </Alert>
              )}

              {showingResults && view.forced_by_dry_run && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Dry-run mode is ON in config</AlertTitle>
                  <AlertDescription>Nothing was sent or refunded. This was a preview.</AlertDescription>
                </Alert>
              )}

              {showingResults && view.counts && (
                <div className="flex items-center gap-3 text-sm px-3 py-2 rounded bg-muted/50">
                  <span className="text-success font-medium">{view.counts.sent} sent</span>
                  <span className="text-destructive font-medium">{view.counts.failed} failed</span>
                  <span className="text-muted-foreground">{view.counts.skipped} skipped</span>
                </div>
              )}

              {/* Decisions */}
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1.5">
                  {showingResults ? "Results" : "Planned actions"} ({view.decisions.length})
                </p>
                {view.decisions.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No first-timer decisions for this event.
                  </p>
                ) : (
                  <ScrollArea className="h-[280px] rounded border px-3">
                    {view.decisions.map((d) => (
                      <DecisionRow key={d.ticket_id} d={d} showOutcome={showingResults} />
                    ))}
                  </ScrollArea>
                )}
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={executing}>
            {showingResults ? "Close" : "Cancel"}
          </Button>
          {!showingResults && preview && preview.decisions.some((d) => !d.skipped) && (
            <Button onClick={runExecute} disabled={executing}>
              {executing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirm & send
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
